import { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import { motion } from "framer-motion";
import { X, MapPin, Phone, Loader2, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One pin per saved address — color tells you the type at a glance.
const TYPE_COLORS = {
  home: "#15803D",
  work: "#2563EB",
  market: "#E85D45",
  warehouse: "#7C3AED",
  other: "#6B7280",
};
const TYPE_LABELS = {
  home: "بيت",
  work: "عمل",
  market: "ماركت",
  warehouse: "مخزن",
  other: "آخر",
};

const makePin = (color = "#15803D", isDefault = false) =>
  L.divIcon({
    className: "",
    iconSize: [40, 52],
    iconAnchor: [20, 52],
    html: `
      <div style="position:relative;text-align:center;">
        <div style="background:${color};color:white;border-radius:50% 50% 50% 0;width:34px;height:34px;display:flex;align-items:center;justify-content:center;margin:0 auto;border:3px solid white;box-shadow:0 4px 8px rgba(0,0,0,0.35);transform:rotate(-45deg);">
          <span style="transform:rotate(45deg);font-size:16px;">📍</span>
        </div>
        ${isDefault ? `<div style="position:absolute;top:-6px;right:-2px;background:#F5A623;color:white;border-radius:50%;width:16px;height:16px;display:flex;align-items:center;justify-content:center;font-size:10px;border:2px solid white;">★</div>` : ""}
      </div>`,
  });

const CustomerLocationsModal = ({ customerId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/customers/${customerId}/locations`);
        setData(d);
      } catch {
        toast.error("فشل تحميل مواقع الزبون");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId, onClose]);

  // Pick the best center: a default saved location → primary lat/lng → Baghdad.
  const center = useMemo(() => {
    if (!data) return [33.3152, 44.3661];
    const def = (data.saved_locations || []).find((l) => l.is_default) || (data.saved_locations || [])[0];
    if (def) return [def.lat, def.lng];
    if (data.customer?.primary_lat && data.customer?.primary_lng) {
      return [data.customer.primary_lat, data.customer.primary_lng];
    }
    return [33.3152, 44.3661];
  }, [data]);

  const allPoints = useMemo(() => {
    if (!data) return [];
    const pts = [];
    (data.saved_locations || []).forEach((l) => {
      pts.push({ ...l, kind: "saved" });
    });
    if (data.customer?.primary_lat && data.customer?.primary_lng) {
      const alreadyHas = pts.some(
        (p) => Math.abs(p.lat - data.customer.primary_lat) < 1e-6 && Math.abs(p.lng - data.customer.primary_lng) < 1e-6
      );
      if (!alreadyHas) {
        pts.push({
          id: "primary",
          name: "الموقع المسجّل بالحساب",
          label_type: "other",
          lat: data.customer.primary_lat,
          lng: data.customer.primary_lng,
          kind: "primary",
        });
      }
    }
    return pts;
  }, [data]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4"
      data-testid="customer-locations-modal"
    >
      <motion.div
        initial={{ y: 20, scale: 0.97 }}
        animate={{ y: 0, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="bg-gradient-to-l from-[#15803D] to-[#126b32] text-white px-5 py-4 flex items-center gap-3">
          <MapPin className="w-5 h-5" />
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-lg truncate">
              مواقع: {data?.customer?.market_name || data?.customer?.full_name || "—"}
            </h2>
            {data?.customer?.phone && (
              <p className="text-xs opacity-90 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {data.customer.phone}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="close-loc-modal"
            className="p-2 hover:bg-white/15 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#15803D]" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Map */}
            <div style={{ height: 360 }} className="border-b border-[#E4EBE6]">
              {allPoints.length > 0 ? (
                <MapContainer
                  center={center}
                  zoom={14}
                  style={{ height: "100%", width: "100%" }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {allPoints.map((p) => (
                    <Marker
                      key={p.id}
                      position={[p.lat, p.lng]}
                      icon={makePin(TYPE_COLORS[p.label_type] || "#6B7280", p.is_default)}
                    >
                      <Popup>
                        <div className="text-right" style={{ minWidth: 180 }}>
                          <p className="font-bold text-sm mb-0.5">{p.name}</p>
                          {p.area_note && <p className="text-xs text-gray-600 mb-0.5">📍 {p.area_note}</p>}
                          {p.alt_phone && <p className="text-xs text-gray-600 mb-0.5">📞 {p.alt_phone}</p>}
                          <p className="text-[10px] text-gray-400 mt-1">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</p>
                          <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-bold text-[#2563EB] hover:underline"
                          >
                            <ExternalLink className="w-3 h-3" />
                            افتح بخرائط جوجل
                          </a>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-[#5B6B62]">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-2 text-[#BAE6FD]" />
                    <p className="font-bold">ما عنده مواقع محفوظة</p>
                    <p className="text-xs">الزبون لم يحدد أي موقع لحد الآن.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Locations list */}
            <div className="p-4 space-y-2">
              <h3 className="font-bold text-sm text-[#5B6B62] mb-2">
                {allPoints.length} موقع
              </h3>
              {allPoints.map((p) => (
                <div
                  key={p.id}
                  className="bg-[#F0F9FF] rounded-xl p-3 flex items-start gap-3"
                  data-testid={`loc-row-${p.id}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0"
                    style={{ background: TYPE_COLORS[p.label_type] || "#6B7280" }}
                  >
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-sm text-[#111814] truncate">{p.name}</p>
                      <span className="bg-white text-[10px] font-bold text-[#2D4A3E] px-2 py-0.5 rounded-full">
                        {TYPE_LABELS[p.label_type] || "آخر"}
                      </span>
                      {p.is_default && (
                        <span className="bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3 fill-current" />
                          الرئيسي
                        </span>
                      )}
                    </div>
                    {p.area_note && (
                      <p className="text-xs text-[#5B6B62] mt-0.5">📍 {p.area_note}</p>
                    )}
                    {p.alt_phone && (
                      <a href={`tel:${p.alt_phone}`} className="text-xs text-[#E85D45] mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {p.alt_phone}
                      </a>
                    )}
                    <p className="text-[10px] text-[#5B6B62] font-mono mt-0.5">
                      {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                    </p>
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${p.lat},${p.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    title="افتح بخرائط جوجل للملاحة"
                    className="bg-white hover:bg-[#E0F2FE] p-2 rounded-full shrink-0 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-[#2563EB]" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default CustomerLocationsModal;
